type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface QuoteDecisionInfo {
  decision: "approved" | "rejected";
  quoteNumber: string;
}

/**
 * Customer quote decision carried by a FrontDesk note (`content_attributes.quote_decision`)
 * or a notification (`metadata.quoteDecision`). Null for anything else.
 */
export function readQuoteDecision(source: unknown): QuoteDecisionInfo | null {
  const attrs =
    source && typeof source === "object"
      ? (source as { quote_decision?: unknown; quoteDecision?: unknown })
      : null;
  const raw = attrs?.quote_decision ?? attrs?.quoteDecision;
  const value =
    raw && typeof raw === "object"
      ? (raw as { decision?: unknown; quote_number?: unknown; quoteNumber?: unknown })
      : null;
  if (value?.decision !== "approved" && value?.decision !== "rejected") {
    return null;
  }
  const number = value.quote_number ?? value.quoteNumber;
  return {
    decision: value.decision,
    quoteNumber: typeof number === "string" ? number.trim() : "",
  };
}

/** The decision sentence in the viewing agent's UI language. */
export function quoteDecisionText(info: QuoteDecisionInfo, t: Translate): string {
  const approved = info.decision === "approved";
  if (!info.quoteNumber) {
    return t(approved ? "documentStatus.ApprovedByClient" : "documentStatus.RejectedByClient").trim();
  }
  return t(
    approved ? "notifications.approvedQuoteMessage" : "notifications.rejectedQuoteMessage",
    { number: info.quoteNumber }
  );
}

/**
 * Text for a customer's quote decision, in the viewing agent's UI language.
 * Null when the message is not a quote decision, so the stored text is shown instead.
 */
export function quoteDecisionActivityText(contentAttributes: unknown, t: Translate): string | null {
  const info = readQuoteDecision(contentAttributes);
  return info ? quoteDecisionText(info, t) : null;
}
